import type { Maybe } from "@entrilia/shared/types.ts";
import { BiAggregation } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type { ReportField } from "../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../api/types/svTypes.ts";
import { defaultDateFormat, defaultNumericFormat, defaultTextFormat } from "../formatters/NumberFormatter.ts";

export function getAggregationTypes() {
  const items = [
    { value: BiAggregation.None, text: "None", short: "None" },
    { value: BiAggregation.Count, text: "Count", short: "Count" },
    { value: BiAggregation.CountDistinct, text: "Count (Distinct)", short: "Count Dist" },
    { value: BiAggregation.Average, text: "Average", short: "Avg" },
    { value: BiAggregation.Min, text: "Min", short: "Min" },
    { value: BiAggregation.Max, text: "Max", short: "Max" },
    { value: BiAggregation.Sum, text: "Sum", short: "Sum" },
    { value: BiAggregation.StringAgg, text: "Concatenate Values", short: "ConcVal" },
  ];

  return items;
}

export function findAggregationTypes(types: BiAggregation[]) {
  return getAggregationTypes().filter((r) => types.includes(r.value));
}

export function getAggregationType(aggregationType: BiAggregation) {
  const found = getAggregationTypes().find((r) => r.value === aggregationType);
  return found;
}

export const getDimensionAggregationType = (field: ReportField) => {
  if (!field.config.aggregation) {
    return undefined;
  }
  if (field.config.aggregation === BiAggregation.None) {
    return undefined;
  }

  return field.config.aggregation;
};

export const getDefaultFormatting = (field: ReportField) => {
  switch (field.meta.type) {
    case ItemDataType.Date:
      return defaultDateFormat;
    case ItemDataType.General:
      return defaultTextFormat;
    case ItemDataType.Numeric:
      return defaultNumericFormat;
    default:
      return undefined;
  }
};

export const isCountRelatedAggregation = (aggregation: Maybe<BiAggregation>) => {
  return aggregation === BiAggregation.Count || aggregation === BiAggregation.CountDistinct;
};

export const supportedTabularAggregations = [
  BiAggregation.None,
  BiAggregation.Average,
  BiAggregation.Count,
  BiAggregation.CountDistinct,
  BiAggregation.Max,
  BiAggregation.Min,
  BiAggregation.StringAgg,
  BiAggregation.Sum,
];

export const getSupportedTabularAggregationTypes = (field: ReportField) => {
  const items = [BiAggregation.None, BiAggregation.Count, BiAggregation.CountDistinct];

  if (field.meta.type === ItemDataType.Numeric) {
    items.push(BiAggregation.Average, BiAggregation.Min, BiAggregation.Max, BiAggregation.Sum);
  } else if (field.meta.type === ItemDataType.Date) {
    items.push(BiAggregation.Min, BiAggregation.Max);
  } else if (field.meta.type === ItemDataType.General) {
    items.push(BiAggregation.StringAgg);
  }

  return items;
};

export const supportedListAggregations = supportedTabularAggregations;
export const getSupportedListAggregationTypes = getSupportedTabularAggregationTypes;

export const supportedTileAggregations = [
  BiAggregation.Average,
  BiAggregation.Count,
  BiAggregation.CountDistinct,
  BiAggregation.Max,
  BiAggregation.Min,
  BiAggregation.Sum,
];

export const getSupportedTileAggregationTypes = (field: ReportField) => {
  const items = [BiAggregation.Count, BiAggregation.CountDistinct];

  if (field.meta.type === ItemDataType.Numeric) {
    items.push(BiAggregation.Average, BiAggregation.Min, BiAggregation.Max, BiAggregation.Sum);
  } else if (field.meta.type === ItemDataType.Date) {
    items.push(BiAggregation.Min, BiAggregation.Max);
  }

  return items;
};

export const supportedPivotAggregations = [
  BiAggregation.Average,
  BiAggregation.Count,
  BiAggregation.CountDistinct,
  BiAggregation.Max,
  BiAggregation.Min,
  BiAggregation.Sum,
];

export const getSupportedPivotAggregationTypes = (field: ReportField) => {
  const items = [BiAggregation.Count, BiAggregation.CountDistinct];

  if (field.meta.type === ItemDataType.Numeric) {
    items.push(BiAggregation.Average, BiAggregation.Min, BiAggregation.Max, BiAggregation.Sum);
  }

  return items;
};

export const supportedChartAggregations = [
  BiAggregation.Average,
  BiAggregation.Count,
  BiAggregation.CountDistinct,
  BiAggregation.Max,
  BiAggregation.Min,
  BiAggregation.Sum,
];

export const getSupportedChartAggregationTypes = (field: ReportField) => {
  const items = [BiAggregation.Count, BiAggregation.CountDistinct];

  if (field.meta.type === ItemDataType.Numeric) {
    items.push(BiAggregation.Average, BiAggregation.Min, BiAggregation.Max, BiAggregation.Sum);
  }

  return items;
};
